import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { AccountType, IAccount, IListResponse, ILoadFundsForm } from '@lib/interfaces';
import { IAccountTransferTransaction, ITransferToAgentRequest } from '@lib/interfaces/super-agent-transfer.interface';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AccountsService {
    environment_url: string;
    reloadAccountsList: EventEmitter<boolean> = new EventEmitter();
    reloadWalletList: EventEmitter<boolean> = new EventEmitter();
    agentsAccounts = signal(new Map<string, string>()); // this will contain agent's account list in (currency, account_id) format

    constructor(private http: HttpClient) {
        this.environment_url = environment.api_url;
    }

    getAccountsByUserId(userId: string, type?: AccountType): Observable<IListResponse<IAccount>> {
        return this.http.get<IListResponse<IAccount>>(`${this.environment_url}nfront/v1/proxy/accounts?user_id=${userId}${type ? `&type=${type}&limit=100` : '&limit=100'}`);
    }

    fetchAccountDetails(accountId: string): Observable<any> {
        return this.http.get<any>(`${this.environment_url}nfront/v1/proxy/accounts/${accountId}`);
    }
    accountDetails(card_product_id: string | null): Observable<any> {
        return this.http.post<any>(`${this.environment_url}nfront/v1/proxy/accounts`, {
            user_id: localStorage.getItem('userId'),
            type: 'PREPAID',
            card_product_id,
        });
    }

    loadFunds(payload: ILoadFundsForm, accountId: string, loadFunds = 'loadFunds'): Observable<any> {
        const headers = new HttpHeaders({
            'x-nymos-idempotency-key': crypto.randomUUID(),
        });
        return this.http.post<any>(`${this.environment_url}nfront/v1/proxy/accounts/${accountId}:${loadFunds}`, payload, { headers });
    }

    /**
     * NEOIQ-2427 — agent-to-agent "Top-up for Agent" transfer.
     *
     * `idempotencyKey` must be generated once per logical attempt by the caller (e.g. `crypto.randomUUID()`)
     * — reusing the same key with the same body returns the original transaction; reusing it with a
     * different body returns 409 Conflict.
     *
     * `passcode` is confirmed by the backend team to be sent as the `otp_auth` header on this single call —
     * there is no separate 401/otp_id challenge round-trip for this endpoint.
     */
    transferToAgent(payload: ITransferToAgentRequest, idempotencyKey: string, passcode: string): Observable<IAccountTransferTransaction> {
        let headers = new HttpHeaders({ 'x-nymos-idempotency-key': idempotencyKey, otp_auth: passcode });

        // NEOIQ-2427: backend identifies the initiating (sender) agent via this header — value is
        // the sender's own user_id, same localStorage key read elsewhere in this file
        // (accountDetails() above). Despite the header name, this isn't a browser/device
        // fingerprint — it's just the sender's user_id, per explicit API contract.
        const senderUserId = localStorage.getItem('userId');
        if (senderUserId) {
            headers = headers.set('x-nfront-device-id', senderUserId);
        }

        return this.http.post<IAccountTransferTransaction>(`${this.environment_url}nfront/v1/proxy/accounts:transferToAgent`, payload, { headers });
    }
}
